// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Button, ButtonProps, styled } from '@mui/material';
import {
  ChangeEventHandler,
  ForwardedRef,
  PropsWithChildren,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

const HiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface Props
  extends Pick<
    ButtonProps,
    'disabled' | 'startIcon' | 'size' | 'sx' | 'variant'
  > {
  /**
   * A callback fired when a file is selected.
   */
  onChange: (file: File) => void;
}

export interface FileInputButtonHandle {
  /**
   * Opens the file picker dialog programmatically.
   */
  openFilePicker: () => void;
}

function FileInputButtonRenderer(
  props: PropsWithChildren<Props>,
  ref: ForwardedRef<FileInputButtonHandle>
) {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    openFilePicker: () => {
      inputRef.current?.click();
    },
  }));

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.files === null) {
      return;
    }

    const file = event.target.files[0];

    props.onChange(file);
  };

  return (
    <Button
      role={undefined}
      size={props.size}
      component="label"
      disabled={props.disabled}
      startIcon={props.startIcon}
      variant={props.variant}
      sx={props.sx}
    >
      {props.children}
      <HiddenInput
        ref={inputRef}
        type="file"
        onChange={handleChange}
        disabled={props.disabled}
      />
    </Button>
  );
}

/**
 * A button that opens a file picker when clicked.
 *
 * Exposes an imperative handle with `openFilePicker()` method to
 * programmatically open the file picker dialog.
 */
export const FileInputButton = forwardRef(FileInputButtonRenderer);
